import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  MotifButton,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
  MotifInput,
  MotifLabel,
  MotifErrorMessage,
} from "@ey-xd/motif-react";
import { PaymentStatusProps, PaymentStatusValue, Status } from "../type";
import {
  useUpdatePaymentStatusMutation,
  useUpdateBulkPaymentStatusMutation,
} from "@/services/paymentStatus/paymentStatusReducer";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import "../PaymentStatus.scss";
import { getOnlyDate, getPaymentStatus } from "@/utility/common";
import CommonTooltipWrapper from "@/components/Tooltip/CommonTooltipWrapper";
import { ALL_STATUSES } from "@/Constant";
const UpdatePaymentStatus = ({
  showModal,
  validationError,
  currentStatusLabel,
  setShowUpdateStatuModal,
  availableStatuses,
  selectedPaymentIds,
  clearSelectedPayemntIds,
  refreshAfterPaymentUpdate,
  allPaymentSelected,
  isFilterActive,
  filterPayload,
  setPaymentSuccessMessage,
}: PaymentStatusProps) => {
  const sameStatusesSelected = selectedPaymentIds.every(
    (payment: { paymentStatus: number }) =>
      getPaymentStatus(payment.paymentStatus) === currentStatusLabel
  );
  const allStatusLabels: string[] = useMemo(
    () => ALL_STATUSES.map((status) => status.title),
    []
  );
  const { t } = useTranslation();
  const { caseId } = useParams();
  const permission = useSelector((state: any) => state.permissions);
  const { ChequeUpdate } = permission;
  const isSpecialAccess = ChequeUpdate && ChequeUpdate.includes("Execute");
  const [isLoading, setIsLoading] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");
  const [postedDate, setPostedDate] = useState<any>("");
  const [postedDateError, setPostedDateError] = useState(false);
  const [updatePaymentStatus] = useUpdatePaymentStatusMutation();
  const [updateBulkPaymentStatus] = useUpdateBulkPaymentStatusMutation();
  const retainState = useSelector((e: any) => e.retainState.paymentsList);

  /**
   * Returns a list of statuses with display and disabled flags based on visibility and hiding rules.
   * @param visibleStatusList - Array of status titles to be displayed and enabled.
   * @param hideStatusList - Optional array of status titles to be hidden.
   * @param displayNonvisibleStatus - Optional boolean to display non-visible statuses as disabled.
   * @returns Array of Status objects with display and disabled properties.
   */
  const getAvailableStatusLabels = useCallback(
    (
      visibleStatusList: string[],
      hideStatusList?: string[],
      displayNonvisibleStatus?: boolean
    ) => {
      return ALL_STATUSES.map((ele: Status) => {
        if (hideStatusList?.includes(ele.title)) {
          return { ...ele, display: false, disabled: false };
        }
        if (visibleStatusList.includes(ele.title)) {
          return { ...ele, display: true, disabled: false };
        }
        if (displayNonvisibleStatus) {
          return { ...ele, display: true, disabled: true };
        }
        return { ...ele, display: false, disabled: false };
      });
    },
    []
  );
  /**
   * Returns a list of payment statuses with display and disabled flags based on user access, current status, and available statuses.
   * @param hasSpecialAccess - Boolean indicating whether the user has special access rights (e.g., can execute cheque updates).
   * @param currentStatus - The current payment status label as a string (e.g., "Void", "Issued").
   * @param statusAvailable - Array of status titles (strings) that are available for update, expected to match the titles in ALL_STATUSES.
   */
  const getAvailableStatusesList = useCallback(
    (
      hasSpecialAccess: boolean,
      currentStatus: string,
      statusAvailable: string[]
    ) => {
      if (hasSpecialAccess && currentStatus === "Void") {
        return getAvailableStatusLabels(["Replaced"]);
      }
      if (hasSpecialAccess) {
        return getAvailableStatusLabels(allStatusLabels, [
          "Replaced",
          currentStatus,
        ]);
      }
      return getAvailableStatusLabels(statusAvailable, [currentStatus], true);
    },
    [allStatusLabels, getAvailableStatusLabels]
  );
  const availableStatusesList: Status[] = useMemo(
    () =>
      getAvailableStatusesList(
        isSpecialAccess,
        currentStatusLabel,
        availableStatuses
      ),
    [
      isSpecialAccess,
      currentStatusLabel,
      availableStatuses,
      getAvailableStatusesList,
    ]
  );

  const processFilterPayload = (payload: any) => {
    const processedPayload: any = {};
    for (const key in payload) {
      if (payload[key] === "") {
        processedPayload[key] = null;
      } else {
        processedPayload[key] = payload[key];
      }
    }
    return processedPayload;
  };
  const [replacementConfirmation, setReplacementConfirmation] = useState({
    status: false,
    message: "",
  });
  const handleSubmit = async (isReplacedPaymentConfirmed: boolean) => {
    setIsLoading(true);
    setErrorMessage("");
    setReplacementConfirmation({
      ...replacementConfirmation,
      message: "",
    });
    if (["Cashed", "Replaced"]?.includes(updatedStatus) && !postedDate) {
      setPostedDateError(true);
      setIsLoading(false);
      return;
    }
    setPostedDateError(false);
    let requestBody: any;
    if (allPaymentSelected) {
      requestBody = {
        updatePaymentStatusRequestDTO: {
          newPaymentStatus:
            PaymentStatusValue[
              updatedStatus as keyof typeof PaymentStatusValue
            ],
          currentPaymentStatus:
            PaymentStatusValue[
              currentStatusLabel as keyof typeof PaymentStatusValue
            ],
          postedDate: postedDate ? new Date(postedDate).toISOString() : null,
          isSpecialAccess: isSpecialAccess,
        },
        isExportAll: true,
        paymentSearchType: isFilterActive ? 1 : 0,
      };

      if (isFilterActive) {
        requestBody.paymentFilterListRequestDTO =
          processFilterPayload(filterPayload);
        requestBody.paymentFilterListRequestDTO.pageNumber =
          retainState?.PageNumber || 0;
        requestBody.paymentFilterListRequestDTO.pageSize =
          retainState?.PageSize || 25;
      } else {
        requestBody.getPaymentRecordsListRequestDTO = {
          caseId: caseId,
        };
      }
    } else {
      requestBody = {
        paymentIds: selectedPaymentIds.map((payment: any) => payment.paymentId),
        currentPaymentStatus:
          PaymentStatusValue[
            currentStatusLabel as keyof typeof PaymentStatusValue
          ],
        newPaymentStatus:
          PaymentStatusValue[updatedStatus as keyof typeof PaymentStatusValue],
        postedDate: postedDate ? postedDate : null,
        isSpecialAccess: isSpecialAccess,
      };
    }
    requestBody.isReplacedPaymentConfirmed = isReplacedPaymentConfirmed;
    if (allPaymentSelected) {
      await updateBulkPaymentStatus(requestBody).then((response: any) => {
        if (response?.data?.hasReplaceChequeWarning) {
          setReplacementConfirmation({
            status: true,
            message: response.data.replaceChequeWarningMessage,
          });
        } else if (response?.data?.isSuccess) {
          setIsLoading(false);
          setShowUpdateStatuModal(false);
          clearSelectedPayemntIds();
          refreshAfterPaymentUpdate(true);
          setReplacementConfirmation({
            status: false,
            message: "",
          });
        } else {
          if (response?.data?.hasReplaceChequeWarning) {
            setReplacementConfirmation({
              status: true,
              message: response.data.replaceChequeWarningMessage,
            });
          } else {
            setErrorMessage(
              response?.error?.data?.message ||
                "Error occured in updating payment status"
            );
          }
        }
      });
    } else {
      await updatePaymentStatus(requestBody).then((response: any) => {
        if (response.data) {
          if (response?.data?.hasReplaceChequeWarning) {
            setReplacementConfirmation({
              status: true,
              message: response.data.replaceChequeWarningMessage,
            });
          } else {
            setIsLoading(false);
            setShowUpdateStatuModal(false);
            clearSelectedPayemntIds();
            refreshAfterPaymentUpdate(true);
            setReplacementConfirmation({
              status: false,
              message: "",
            });
          }
        } else {
          setIsLoading(false);

          if (response?.data?.hasReplaceChequeWarning) {
            setReplacementConfirmation({
              status: true,
              message: response.data.replaceChequeWarningMessage,
            });
          } else if (response.error.data?.title) {
            setErrorMessage(response.error.data.title);
          } else {
            setErrorMessage(response.error.data?.message);
          }
        }
      });
    }
    setIsLoading(false);
  };
  function handlePostedValuesBasedOnStatus(selectedStatus: string) {
    if (["Void", "Undelivered", "Stop", "Issued"]?.includes(selectedStatus)) {
      setPostedDate("");
    } else if (selectedStatus === "Replaced") {
      if (selectedPaymentIds.length === 1 && !allPaymentSelected) {
        return setPostedDate(
          selectedPaymentIds?.[0]?.issueDate || getOnlyDate(new Date())
        );
      } else {
        setPostedDate(getOnlyDate(new Date()));
      }
    } else if (selectedStatus === "Cashed") {
      setPostedDate(getOnlyDate(new Date()));
    }
  }
  return (
    <MotifModal
      onClose={() => {
        setShowUpdateStatuModal(false);
        setPaymentSuccessMessage &&
          setPaymentSuccessMessage({ show: false, message: "", type: "" });
      }}
      className="payment-status-popup-container"
      show={showModal}
      size="lg"
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: "none",
        },
      }}
    >
      <MotifModalHeader
        closeButtonProps={{
          "aria-label": "Custom Close Button aria-label",
          title: "Custom Close Button title",
        }}
      >
        {sameStatusesSelected
          ? t("pages.paymentStatus.updateStatusLabelWithStatus", {
              currentStatus: currentStatusLabel,
            })
          : t("pages.paymentStatus.updateStatusLabel")}
      </MotifModalHeader>
      {validationError && (
        <div className="mar-b-20">
          <NotificationGrid
            show={true}
            message={validationError}
            type="error"
          />
        </div>
      )}

      {sameStatusesSelected ? (
        <>
          <MotifModalBody className="pad-t-10">
            <div className="status-div">
              {t("pages.paymentStatus.updateStatusToLabel")}
            </div>
            <div className="status-buttons-container">
              {availableStatusesList.map((ele: Status) => {
                return ele.display ? (
                  <CommonTooltipWrapper
                    key={ele.title}
                    tooltipTitle={t(
                      ele.disabled
                        ? "pages.paymentStatus.addStatusInfoMessage"
                        : ""
                    )}
                    placement="top"
                    hide={!ele.disabled}
                  >
                    <MotifButton
                      className={`status-button ${
                        updatedStatus === ele.title ? "active-status" : ""
                      }`}
                      variant="primary-alt"
                      onClick={() => {
                        setUpdatedStatus(ele.title);
                        handlePostedValuesBasedOnStatus(ele.title);
                      }}
                      disabled={ele.disabled}
                    >
                      {ele.title}
                    </MotifButton>
                  </CommonTooltipWrapper>
                ) : null;
              })}
            </div>
            {!["Void", "Undelivered", "Stop"]?.includes(updatedStatus) &&
              updatedStatus && (
                <div className="motif-row">
                  <div className="motif-col-md-4 posted-date-con">
                    <MotifLabel
                      className={
                        ["Cashed", "Replaced"]?.includes(updatedStatus)
                          ? "required-field"
                          : ""
                      }
                      aria-label="Input"
                      htmlFor="input"
                    >
                      {t("pages.paymentStatus.postedDate")}
                    </MotifLabel>
                    <MotifInput
                      //@ts-ignore
                      type="date"
                      hideClearButton={false}
                      onChange={(e: any) => setPostedDate(e.target.value)}
                      value={postedDate}
                    />
                    {postedDateError &&
                      !postedDate &&
                      ["Cashed", "Replaced"]?.includes(updatedStatus) && (
                        <MotifErrorMessage>
                          {t("pages.paymentStatus.postedDateRequired")}
                        </MotifErrorMessage>
                      )}
                  </div>
                </div>
              )}
            {errorMessage && (
              <div className="mar-t-10">
                <NotificationGrid
                  show={true}
                  message={errorMessage}
                  type={"error"}
                />
              </div>
            )}
            {replacementConfirmation.message && (
              <div className="mar-t-10">
                <NotificationGrid
                  show={true}
                  message={replacementConfirmation.message}
                  type={"warning"}
                  onClose={() =>
                    setReplacementConfirmation({
                      ...replacementConfirmation,
                      message: "",
                    })
                  }
                />
              </div>
            )}
          </MotifModalBody>
          <MotifModalFooter>
            <div className="document-footer display-flex ">
              <MotifButton
                type="button"
                variant="secondary"
                className="mar-r-10"
                onClick={() => {
                  setReplacementConfirmation({
                    status: false,
                    message: "",
                  });
                  setShowUpdateStatuModal(false);
                  setPaymentSuccessMessage &&
                    setPaymentSuccessMessage({
                      show: false,
                      message: "",
                      type: "",
                    });
                }}
              >
                {t("pages.paymentStatus.filterForm.cancel")}
              </MotifButton>
              <MotifButton
                onClick={() => handleSubmit(replacementConfirmation.status)}
                variant="primary"
                disabled={isLoading || !updatedStatus}
                type={isLoading ? "loader" : "submit"}
              >
                {replacementConfirmation.status
                  ? t("global.globalActions.confirm")
                  : t("pages.paymentStatus.filterForm.save")}
              </MotifButton>
            </div>
          </MotifModalFooter>
        </>
      ) : null}
    </MotifModal>
  );
};
export default UpdatePaymentStatus;
